import {
  useQAPairVisible,
  useQAPairAIReplyError,
  useQAPairAICanNotReply,
  hasOverQuery,
  useQAPairOpened,
  useQAPairManagerPermission,
  QAPairSupportMessageTypes,
} from './useQAPair';
import {
  useQAPairSubmitQueryMsgIdsRecord,
  useQAPairSubmitAnswersMsgIdsRecord,
} from './useQAPairRecord';
import { useMessageList } from '../message';
import { useMemo } from 'react';
import { useGetMessageQAPairSource } from './useGetMessageQAPairSource';

/**
 * Modal 客服路径可选择问题/答案弹窗
 * AdjustRelate 主管路径调整知识（可能不存在知识）
 * ModifyOnChat 主管路径调整知识（已经提交过知识）
 */
type TriggerType = 'Modal' | 'AdjustRelate' | 'ModifyOnChat';

/**
 * 人工回复侧
 *
 * 获取消息底部QA Pair按钮信息
 */
export const useMessageFooterQAPairBtnInfo = (msg: Conversation.Message) => {
  const qaPairVisible = useQAPairVisible();
  const aiReplyErrorPermission = useQAPairAIReplyError();
  const aiCanNotReplyPermission = useQAPairAICanNotReply();
  const managerPermission = useQAPairManagerPermission();
  const getMessageQAPairSource = useGetMessageQAPairSource();
  const qaPairSubmitQueryMsgIdsRecord = useQAPairSubmitQueryMsgIdsRecord();
  const qaPairSubmitAnswersMsgIdsRecord = useQAPairSubmitAnswersMsgIdsRecord();

  const messageList = useMessageList();
  const qaPairOpened = useQAPairOpened();

  return useMemo(() => {
    if (!QAPairSupportMessageTypes.includes(msg.messageType)) {
      return;
    }

    const result = getMessageQAPairSource(msg);
    if (!result || !qaPairOpened) {
      return;
    }

    const { source, knowledgeQuery } = result;

    if (!knowledgeQuery?.extraInfo) {
      return;
    }

    if (source === 'CUSTOMER' || source === 'HISTORY_CUSTOMER') {
      const isSubmitted =
        qaPairSubmitQueryMsgIdsRecord.includes(msg.msgId) ||
        qaPairSubmitAnswersMsgIdsRecord.includes(msg.msgId);

      const mergePermission = aiCanNotReplyPermission || managerPermission;
      let enable = mergePermission;
      if (knowledgeQuery) {
        enable = mergePermission && hasOverQuery(messageList, knowledgeQuery);
      }

      let triggerType: TriggerType = 'Modal';

      if (managerPermission && isSubmitted) {
        triggerType = 'ModifyOnChat';
      }

      return {
        enable,
        visible: qaPairVisible,
        isSubmitted,
        source,
        knowledgeQuery,
        triggerType,
      } as const;
    }
    if (source === 'AI_ERROR_REPLY') {
      const isSubmitted =
        qaPairSubmitQueryMsgIdsRecord.includes(msg.msgId) ||
        qaPairSubmitAnswersMsgIdsRecord.includes(msg.msgId);
      const mergePermission = aiReplyErrorPermission || managerPermission;

      let triggerType: TriggerType = 'Modal';
      if (managerPermission && isSubmitted) {
        triggerType = 'ModifyOnChat';
      }
      return {
        enable: isSubmitted ? mergePermission : aiReplyErrorPermission,
        visible: qaPairVisible,
        isSubmitted,
        source,
        knowledgeQuery,
        triggerType,
      } as const;
    }
  }, [
    getMessageQAPairSource,
    msg,
    qaPairOpened,
    qaPairSubmitQueryMsgIdsRecord,
    qaPairSubmitAnswersMsgIdsRecord,
    aiCanNotReplyPermission,
    managerPermission,
    qaPairVisible,
    messageList,
    aiReplyErrorPermission,
  ]);
};
